import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { AirplaneFilled, FlagFilled } from "@fluentui/react-icons";
import React from "react";
import {
  $greenStatus,
  $redStatus,
  $sky50,
  $yellowStatus,
} from "../../cssjs/colors";
import { FlightDetailsData } from "../../models/flights/IFlightsCommon";
import { formatDisplayValue } from "../../utils/common/FormatHelper";
import FlightNumberCellLink from "../flights/flight-details/flight-table/FlightNumberCellLink";
import { FlightStatus } from "../shared/components/airport-cards/flights/FlightStatus";
import { useCardStyles } from "./shared.styles";

interface FlightDetailsCardProps {
  data: FlightDetailsData;
  airport: string;
  className?: string;
}

const FlightDetailsCard: React.FC<FlightDetailsCardProps> = ({
  data,
  airport,
  className,
}) => {
  const shared = useCardStyles();
  const local = useLocalStyles();

  // Helper to determine the color class based on the status text
  const getStatusColorClass = () => {
    const statusText = (data.status || "").toLowerCase();
    if (statusText.includes("delay")) {
      return local.statusDelayed;
    }
    if (statusText.includes("time") || statusText.includes("early")) {
      return local.statusOnTime;
    }
    if (statusText.includes("cancel")) {
      return local.statusCancelled;
    }
    return local.statusDefault;
  };

  return (
    <article
      className={mergeClasses(shared.container, local.container, className)}
    >
      <section className={local.leftSection}>
        <header className={mergeClasses(shared.header, local.header)}>
          <h2 className={mergeClasses(shared.largeNumber, local.flightNumber)}>
            {formatDisplayValue(data.flightNumber)}
          </h2>
          <hgroup className={shared.headerTitleGroup}>
            <AirplaneFilled className={shared.headerIcon} />
            <h3 className={shared.headerTitle}>Flight Details</h3>
          </hgroup>
        </header>

        <div className={local.routeContainer}>
          <FlightStatus
            origin={data.origin}
            destination={data.destination}
            progressPercentage={data.progressPercentage ?? 0}
            isDetails={true}
          />
          <p className={mergeClasses(local.statusText, getStatusColorClass())}>
            {formatDisplayValue(data.remarkText)}
          </p>
        </div>
      </section>

      <section className={local.rightSection}>
        <div className={local.topRight}>
          <span className={local.flightTypeText}>
            {`${formatDisplayValue(data.legType)} • ${formatDisplayValue(
              data.flightType
            )}`}
          </span>
          <FlagFilled className={local.flagIcon} />
        </div>

        <dl className={local.infoList}>
          <div className={local.infoRow}>
            <dt className={local.infoLabel}>Airline:</dt>
            <dd className={local.infoValue}>
              {formatDisplayValue(data.airline)}
            </dd>
          </div>
          <div className={local.infoRow}>
            <dt className={local.infoLabel}>Operating Carrier:</dt>
            <dd className={local.infoValue}>
              {formatDisplayValue(data.operatingCarrier)}
            </dd>
          </div>
          <div className={local.infoRow}>
            <dt className={local.infoLabel}>A/C Type:</dt>
            <dd className={local.infoValue}>
              {formatDisplayValue(data.acType)}
            </dd>
          </div>
          <div className={local.infoRow}>
            <dt className={local.infoLabel}>A/C Reg:</dt>
            <dd className={local.infoValue}>
              {formatDisplayValue(data.acReg)}
            </dd>
          </div>
        </dl>

        <div className={local.linkedFlightContainer}>
          <span className={local.linkedFlightLabel}>Linked Flight:</span>
          <span className={local.linkedFlightValue}>
            {data.linkedFlight ? (
              <FlightNumberCellLink
                airport={airport}
                flightNumber={data.linkedFlight}
                docId={data.linkedFlightId ?? ""}
              />
            ) : (
              formatDisplayValue(data.linkedFlight)
            )}
          </span>
        </div>
      </section>
    </article>
  );
};

export default FlightDetailsCard;

const useLocalStyles = makeStyles({
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "16px",
    minWidth: 0,
  },
  leftSection: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    flex: "1 1 200px",
    minWidth: 0,
  },
  header: {
    marginBottom: 0,
  },
  flightNumber: {
    fontSize: "40px",
    margin: 0,
  },
  routeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
    maxWidth: "200px",
    boxSizing: "border-box",
    paddingTop: "52px",
    paddingLeft: "14px",
  },

  statusText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: "center",
    margin: 0,
  },
  statusDelayed: {
    color: $yellowStatus,
  },
  statusOnTime: {
    color: $greenStatus,
  },
  statusCancelled: {
    color: $redStatus,
  },
  statusDefault: {
    color: tokens.colorNeutralForeground1,
  },

  rightSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-end",
    height: "100%",
    flex: "1 1 262px",
    minWidth: 0,
  },
  topRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    maxWidth: "100%",
  },
  flightTypeText: {
    fontSize: tokens.fontSizeBase200,
    overflowWrap: "anywhere",
    textAlign: "right",
  },
  flagIcon: {
    fontSize: "16px",
  },
  infoList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    margin: 0,
    marginRight: "24px",
    width: "calc(100% - 24px)",
    maxWidth: "238px",
    minWidth: 0,
  },
  infoRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    width: "100%",
    minWidth: 0,
  },
  infoLabel: {
    flex: "1 1 130px",
    minWidth: 0,
    textAlign: "right",
    fontSize: tokens.fontSizeBase200,
    margin: 0,
  },
  infoValue: {
    flex: "0 1 100px",
    minWidth: 0,
    textAlign: "left",
    fontSize: tokens.fontSizeBase200,
    margin: 0,
    overflowWrap: "anywhere",
  },
  linkedFlightContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    width: "100%",
    minWidth: 0,
  },
  linkedFlightLabel: {
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
  },
  linkedFlightValue: {
    fontSize: tokens.fontSizeBase200,
    color: $sky50,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
});
